import {
  Box,
  Button,
  Checkbox,
  Circle,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Portal,
  Select,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  Tooltip,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { AddIcon, ChevronDownIcon, DeleteIcon, EditIcon, ViewIcon, ViewOffIcon, WarningIcon } from "@chakra-ui/icons";
import { FilterProto, Header, MetricProto, ObservationColumnProto } from "build-ts/metadata";
import React, { useEffect, useMemo, useState } from "react";
import { getMetricById, listMetrics } from "services/MetricService";
import showToast from "hooks/useCustomToast";
import { ColumnType, FilterOperator, MetadataTypeEnum, MetricEdgeType } from "build-ts/enums";
import { KGObservationProto } from "build-ts/analysis_plan";
import { getKGObservationData } from "services/AnalysisTreeServices";
import ColumnValues from "components/common/ColumnValues";
import { AnalystProto, ColumnScopeProto } from "build-ts/analyst";
import { KGColumnProto } from "build-ts/knowledege_graph";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { getTooltip } from "utils/getMessages";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { refreshAnalystDataScope } from "services/AnalystService";
import { FaSync } from "react-icons/fa";
import { useUser } from "context/UserContext";

interface DataScopeProps {
  originalAnalyst?: AnalystProto;
  instructions: string;
  setInstructions: (value: string) => void;
  useDefaultScope: boolean;
  setUseDefaultScope: (value: boolean) => void;
  selectedMetricsWithEdges: MetricProto[];
  setSelectedMetricsWithEdges: React.Dispatch<React.SetStateAction<MetricProto[]>>;
  selectedBenchmarks: Header[];
  setSelectedBenchmarks: React.Dispatch<React.SetStateAction<Header[]>>;
  columnScopes: ColumnScopeProto[];
  setColumnScopes: React.Dispatch<React.SetStateAction<ColumnScopeProto[]>>;
  isCreatingNew?: boolean;
}

const DataScopeConfigurator: React.FC<DataScopeProps> = ({
  originalAnalyst,
  instructions,
  setInstructions,
  useDefaultScope,
  setUseDefaultScope,
  selectedMetricsWithEdges,
  setSelectedMetricsWithEdges,
  selectedBenchmarks,
  setSelectedBenchmarks,
  columnScopes,
  setColumnScopes,
  isCreatingNew = false,
}) => {

  // User context
  const { defaultTimeZone } = useUser();
  // Local state for UI only
  const [listOfMetrics, setListOfMetrics] = useState<Header[]>([]);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState<boolean>(false);
  const [isLoadingEdges, setIsLoadingEdges] = useState<Map<string, boolean>>(new Map());

  // Derived state from selectedMetricsWithEdges
  const selectedMetricHeaders = useMemo(
    () => selectedMetricsWithEdges.map(m => m.header).filter((h): h is Header => h !== undefined),
    [selectedMetricsWithEdges]
  );

  // Lists of available dependent headers (union from selected metrics)
  const [benchmarkList, setBenchmarkList] = useState<Header[]>([]);
  const [measureList, setMeasureList] = useState<Header[]>([]);
  const [timeframeList, setTimeframeList] = useState<Header[]>([]);
  const [attributeList, setAttributeList] = useState<Header[]>([]);

  // Derived selected items from columnScopes
  const selectedMeasures = useMemo(
    () => columnScopes
      .filter(f => f?.kg_column?.header?.column_type === ColumnType.MEASURE)
      .map(f => f?.kg_column?.header)
      .filter((h): h is Header => h !== undefined),
    [columnScopes]
  );

  const selectedTimeframes = useMemo(
    () => columnScopes
      .filter(f => f?.kg_column?.header?.column_type === ColumnType.TEMPORAL)
      .map(f => f?.kg_column?.header)
      .filter((h): h is Header => h !== undefined),
    [columnScopes]
  );

  const selectedAttributes = useMemo(
    () => columnScopes
      .filter(f => f?.kg_column?.header?.column_type === ColumnType.ATTRIBUTE)
      .map(f => f?.kg_column?.header)
      .filter((h): h is Header => h !== undefined),
    [columnScopes]
  );

  // Attribute filters only (for "Applied Filters" section)
  const attributeFilters = useMemo(
    () => columnScopes.filter(f => f?.kg_column?.header?.column_type === ColumnType.ATTRIBUTE),
    [columnScopes]
  );

  // Filter state for editing
  const [valuesList, setValuesList] = useState<string[]>([]);
  const [isLoadingValues, setIsLoadingValues] = useState<boolean>(false);
  const [showValuesSection, setShowValuesSection] = useState<boolean>(false);
  const [selectedFilterOperator, setSelectedFilterOperator] = useState<FilterOperator>(FilterOperator.EQ);
  const [selectedFilterValues, setSelectedFilterValues] = useState<string[]>([]);
  const [editingFilter, setEditingFilter] = useState<ColumnScopeProto | null>(null);
  const [editingFilterIndex, setEditingFilterIndex] = useState<number>(-1);

  // Modal state for adding/editing filters
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [isEditingExistingFilter, setIsEditingExistingFilter] = useState<boolean>(false);

  // Reset state when creating new analyst
  useEffect(() => {
    if (isCreatingNew) {
      setInstructions("");
      setUseDefaultScope(true);
      setSelectedMetricsWithEdges([]);
      setSelectedBenchmarks([]);
      setColumnScopes([]);
    }
  }, [isCreatingNew]);

  // Fetch metrics list when useDefaultScope is false
  useEffect(() => {
    if (!useDefaultScope) {
      fetchMetricsHeaders();
    }
  }, [useDefaultScope]);

  const fetchMetricsHeaders = async () => {
    try {
      setIsLoadingMetrics(true);
      const metrics = await listMetrics("");
      setListOfMetrics(metrics || []);
    } catch (error) {
      showToast("Failed to fetch Metrics", "", "error");
    } finally {
      setIsLoadingMetrics(false);
    }
  };

  // Build union lists from selected metrics
  useEffect(() => {
    if (selectedMetricsWithEdges.length === 0) {
      // Clear everything when no metrics
      setBenchmarkList([]);
      setMeasureList([]);
      setTimeframeList([]);
      setAttributeList([]);
      setSelectedBenchmarks([]);
      setColumnScopes([]);
      return;
    }

    // Build union of all headers from all metrics
    const allHeadersMap = new Map<string, Header>();

    selectedMetricsWithEdges.forEach(metric => {
      metric.edges?.forEach((edge) => {
        if (edge.header && edge.header.id) {
          // Only include relevant edge types
          if (
            edge.edge_type === MetricEdgeType.METRIC_BENCHMARK_EDGE ||
            edge.edge_type === MetricEdgeType.METRIC_ATTRIBUTE_EDGE ||
            edge.edge_type === MetricEdgeType.METRIC_MEASURE_EDGE ||
            edge.edge_type === MetricEdgeType.METRIC_TIMEFRAME_EDGE
          ) {
            allHeadersMap.set(edge.header.id, edge.header);
          }
        }
      });
    });

    // Create union lists from all headers
    const allHeaders = Array.from(allHeadersMap.values());

    const newBenchmarkList = allHeaders.filter((header) => header.type === MetadataTypeEnum.BENCHMARK);
    const newMeasureList = allHeaders.filter((header) => header.column_type === ColumnType.MEASURE);
    const newTimeframeList = allHeaders.filter((header) => header.column_type === ColumnType.TEMPORAL);
    const newAttributeList = allHeaders.filter((header) => header.column_type === ColumnType.ATTRIBUTE);

    // Update all lists
    setBenchmarkList(newBenchmarkList);
    setMeasureList(newMeasureList);
    setTimeframeList(newTimeframeList);
    setAttributeList(newAttributeList);

    // Clean up selections that are no longer available
    setSelectedBenchmarks(prev =>
      prev.filter(h => newBenchmarkList.some(item => item.id === h.id))
    );

    setColumnScopes(prev =>
      prev.filter(filter => {
        const header = filter?.kg_column?.header;
        if (!header?.id) return true;
        if (header.column_type === ColumnType.MEASURE) return newMeasureList.some(m => m.id === header.id);
        if (header.column_type === ColumnType.TEMPORAL) return newTimeframeList.some(t => t.id === header.id);
        if (header.column_type === ColumnType.ATTRIBUTE) return newAttributeList.some(a => a.id === header.id);
        return true;
      })
    );
  }, [selectedMetricsWithEdges]);

  const isMetricSelected = (metricId?: string) =>
    !!metricId && selectedMetricHeaders.some((metric) => metric.id === metricId);

  const toggleMetricSelection = async (metric: Header) => {
    if (!metric.id) return;
    const isCurrentlySelected = selectedMetricHeaders.some((item) => item.id === metric.id);

    if (isCurrentlySelected) {
      // Remove metric
      setSelectedMetricsWithEdges(prev => prev.filter(m => m.header?.id !== metric.id));
    } else {
      // Add metric - fetch with edges
      await fetchMetricWithEdges(metric.id);
    }
  };

  const fetchMetricWithEdges = async (metricId: string) => {
    setIsLoadingEdges(prev => {
      const newMap = new Map(prev);
      newMap.set(metricId, true);
      return newMap;
    });

    try {
      const metric = await getMetricById(metricId, true);
      if (metric) {
        setSelectedMetricsWithEdges(prev => [...prev, metric]);
      } else {
        showToast("Failed to fetch Metric", "", "error");
      }
    } catch (error) {
      showToast("Failed to fetch Metric edges", "", "error");
    } finally {
      setIsLoadingEdges(prev => {
        const newMap = new Map(prev);
        newMap.set(metricId, false);
        return newMap;
      });
    }
  };

  const removeSelectedMetric = (metricId?: string) => {
    if (!metricId) return;
    setSelectedMetricsWithEdges(prev => prev.filter(m => m.header?.id !== metricId));
  };

  const isAnyEdgesLoading = useMemo(
    () => Array.from(isLoadingEdges.values()).some(Boolean),
    [isLoadingEdges]
  );

  const toggleBenchmarkSelection = (header: Header) => {
    if (!header.id) return;
    setSelectedBenchmarks(prev =>
      prev.some(item => item.id === header.id)
        ? prev.filter(item => item.id !== header.id)
        : [...prev, header]
    );
  };

  const toggleMeasureSelection = (header: Header) => {
    if (!header.id) return;
    const isSelected = selectedMeasures.some(m => m.id === header.id);

    if (isSelected) {
      // Remove from columnScopes
      setColumnScopes(prev => prev.filter(f => f?.kg_column?.header?.id !== header.id));
    } else {
      // Add to columnScopes
      setColumnScopes(prev => [...prev, ColumnScopeProto.create({
        kg_column: KGColumnProto.create({ header: header }),
      })]);
    }
  };

  const toggleTimeframeSelection = (header: Header) => {
    if (!header.id) return;
    const isSelected = selectedTimeframes.some(t => t.id === header.id);

    if (isSelected) {
      // Remove from columnScopes
      setColumnScopes(prev => prev.filter(f => f?.kg_column?.header?.id !== header.id));
    } else {
      // Add to columnScopes
      setColumnScopes(prev => [...prev, ColumnScopeProto.create({
        kg_column: KGColumnProto.create({ header: header }),
      })]);
    }
  };

  const toggleAttributeSelection = (header: Header) => {
    if (!header.id) return;
    const isSelected = selectedAttributes.some(a => a.id === header.id);

    if (isSelected) {
      // Remove all filters for this attribute from columnScopes
      setColumnScopes(prev => prev.filter(f => f?.kg_column?.header?.id !== header.id));
    } else {
      // Add to columnScopes without operator/values (entire column scope)
      setColumnScopes(prev => [...prev, ColumnScopeProto.create({
        kg_column: KGColumnProto.create({ header: header }),
      })]);
    }
  };

  // Remove filter means set operator and values to undefined and empty array
  const handleRemoveFilter = (filterId: string) => {
    setColumnScopes(prev =>
      prev.map(f =>
        f?.kg_column?.header?.id === filterId
          ? { ...f, operator: undefined, values: [] }
          : f
      )
    );
  };

  const handleFilterHidden = (filterId: string) => {
    setColumnScopes(prev =>
      prev.map(f =>
        f?.kg_column?.header?.id === filterId
          ? { ...f, is_hidden: !f.is_hidden }
          : f
      )
    );
  };

  const fetchValuesForEditingFilter = async (filter: ColumnScopeProto, filterIndex: number) => {
    if (!filter?.kg_column) return;

    setIsLoadingValues(true);
    setEditingFilter(filter);

    const filtersAbove = columnScopes.slice(0, filterIndex);
    const filtersBelow = columnScopes.slice(filterIndex + 1);

    const validFilters = filtersAbove.filter(f =>
      f.operator !== undefined &&
      f.values &&
      f.values.length > 0
    );

    const validFiltersBelow = filtersBelow.filter(f =>
      f.operator !== undefined &&
      f.values &&
      f.values.length > 0
    );

    try {
      const filtersForRequest = [...validFilters, ...validFiltersBelow].map(f => ({
        ...f,
        is_hidden: false
      }));

      const request = KGObservationProto.create({
        metrics: selectedMetricsWithEdges,
        columns: [ObservationColumnProto.create({
          column_id: filter.kg_column?.header?.id,
          kg_column_header: filter.kg_column?.header,
        })],
        filters: filtersForRequest.map(f => FilterProto.create({
          filter_id: f.kg_column?.header?.id,
          kg_column_header: f.kg_column?.header,
          operator: f.operator,
          values: f.values,
          is_hidden: f.is_hidden,
        })),
        calculate_non_measure_only: true,
      });

      const values = await getKGObservationData([request]);
      const valuesList = JSON.parse(values?.[0]?.kg_observation_data?.non_measure_values[0]?.data_value_json_array || "[]");
      setValuesList((valuesList || []).map(String));

      setEditingFilterIndex(filterIndex);
      setSelectedFilterOperator(filter.operator || FilterOperator.EQ);
      setSelectedFilterValues(filter.values || []);
      setShowValuesSection(true);
    } catch (error) {
      console.error("Error fetching attribute values for editing:", error);
      showToast("Failed to fetch values", "", "error");
      setValuesList([]);
    } finally {
      setIsLoadingValues(false);
    }
  };

  const handleSaveEditedFilter = () => {
    if (!editingFilter || editingFilterIndex === -1 || selectedFilterValues.length === 0) {
      showToast("Please select values", "You must select at least one value to save the filter", "warning");
      return;
    }

    setColumnScopes(prev => {
      const updated = [...prev];
      updated[editingFilterIndex] = ColumnScopeProto.create({
        kg_column: KGColumnProto.create({ header: editingFilter.kg_column?.header }),
        operator: selectedFilterOperator,
        values: selectedFilterValues,
      });
      return updated;
    });

    // Reset editing state
    setEditingFilter(null);
    setEditingFilterIndex(-1);
    setSelectedFilterValues([]);
    setSelectedFilterOperator(FilterOperator.EQ);
    setShowValuesSection(false);
    setValuesList([]);

    showToast("Filter updated", "Filter has been updated successfully", "success");
  };


  // Modal functions
  const openFilterModal = () => {
    setIsEditingExistingFilter(false);
    setIsFilterModalOpen(true);
  };

  const openFilterModalForEdit = (filter: ColumnScopeProto, index: number) => {
    setIsEditingExistingFilter(true);
    setIsFilterModalOpen(true);
    // Fetch values for the filter
    fetchValuesForEditingFilter(filter, index);
  };

  const closeFilterModal = () => {
    setIsFilterModalOpen(false);
    setIsEditingExistingFilter(false);
    // Reset editing state when closing modal
    setEditingFilter(null);
    setEditingFilterIndex(-1);
    setSelectedFilterValues([]);
    setSelectedFilterOperator(FilterOperator.EQ);
    setShowValuesSection(false);
    setValuesList([]);
  };

  const handleAttributeSelectionInModal = (filter: ColumnScopeProto, index: number) => {
    // Use existing fetchValuesForEditingFilter function
    fetchValuesForEditingFilter(filter, index);
  };

  const handleSaveFilterFromModal = () => {
    // Use existing handleSaveEditedFilter function and close modal
    handleSaveEditedFilter();
    closeFilterModal();
  };

  const renderSelectedTags = (
    headers: Header[],
    onRemove: (header: Header) => void
  ) => {
    if (headers.length === 0) return null;
    return (
      <Box mt={3}>
        <Wrap spacing={3}>
          {headers.map((header) => (
            <WrapItem key={header.id}>
              <Tag size="md" borderRadius="lg" colorScheme="purple" variant="solid" py={1} bg="purple.50" borderWidth="1px" borderColor="purple.300">
                <TagLabel fontWeight="700" color="gray.800">{header?.name}</TagLabel>
                <TagCloseButton onClick={() => onRemove(header)} color="gray.800" />
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      </Box>
    );
  };

  const renderDependentHeaderMenu = (
    label: string,
    headers: Header[],
    selected: Header[],
    onToggle: (header: Header) => void
  ) => {
    if (headers.length === 0) return null;

    const isSelected = (header: Header) => {
      if (!header?.id) return false;
      return selected.some((item) => item.id === header.id);
    };

    return (
      <FormControl>
        <HStack justify="space-between" align="center">
          <HStack>
            <Text fontSize="md" fontWeight="600" color="gray.800" textAlign="left" mb={0}>
              {label}
            </Text>
          </HStack>
          <HStack>
            {label === "Attributes" && (
              <HStack spacing={2} justify="flex-end">
                {/* Refresh button with tooltip showing last refresh time */}
                {(!isCreatingNew) && (
                  <Tooltip
                    label={
                      originalAnalyst?.data_scope?.last_refresh_time_ms
                        ? `Last synced: ${epochConverterWithTimeZone(originalAnalyst.data_scope.last_refresh_time_ms, "dd-MMM-YYYY, HH:mm", defaultTimeZone)} ${defaultTimeZone}`
                        : "Never synced"
                    }
                    placement="top"
                    hasArrow
                  >
                    <Button
                      colorScheme="purple"
                      variant="solid"
                      bg="purple.50"
                      color="fg.default"
                      size="xs"
                      onClick={() => {
                        refreshAnalystDataScope(originalAnalyst?.header?.id || "", true);
                      }}
                      leftIcon={<Icon as={FaSync} boxSize={3} />}
                      fontSize="xs"
                      fontWeight="600"
                      borderRadius="lg"
                      _hover={{ color: "white", bg: "purple.600" }}
                    >
                      Sync
                    </Button>
                  </Tooltip>
                )}
              </HStack>
            )}
            <Menu closeOnSelect={false} autoSelect={false}>
              <MenuButton
                as={Button}
                size="sm"
                rightIcon={<ChevronDownIcon />}
                variant="outline"
                colorScheme="purple"
                isDisabled={headers.length === 0}
              >
                {selected.length} of {headers.length} Selected
              </MenuButton>
              <Portal>
                <MenuList maxH="300px" overflowY="auto" minW="280px" px={1} zIndex="dropdown">
                  {headers.map((header) => (
                    <MenuItem
                      key={header.id}
                      py={2}
                      _hover={{ bg: "purple.50" }}
                      _focus={{ bg: "purple.50" }}
                      onClick={(event) => {
                        event.stopPropagation();
                        onToggle(header);
                      }}
                    >
                      <HStack spacing={3} align="center" w="100%">
                        <Checkbox
                          isChecked={isSelected(header)}
                          colorScheme="purple"
                          pointerEvents="none"
                          flexShrink={0}
                          m={0}
                        />
                        <Text fontSize="sm" fontWeight="500" color="gray.700" m={0}>
                          {header.name}
                        </Text>
                      </HStack>
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
          </HStack>
        </HStack>
        {renderSelectedTags(selected, (header) => onToggle(header))}
      </FormControl>
    );
  };

  return (
    <Box
      maxW="1400px"
      mx="auto"
      h="100%"
      overflowY="auto"
      px={8}
      py={6}
      sx={{
        "&::-webkit-scrollbar": {
          width: "6px"
        },
        "&::-webkit-scrollbar-track": {
          bg: "transparent"
        },
        "&::-webkit-scrollbar-thumb": {
          bg: "gray.300",
          borderRadius: "full"
        },
        "&::-webkit-scrollbar-thumb:hover": {
          bg: "gray.400"
        },
        scrollbarWidth: "thin",
        scrollbarColor: "var(--chakra-colors-gray-300) transparent"
      }}
    >
      <VStack
        align="stretch"
        spacing={5}
        // Add these properties for vertical centering
        h={useDefaultScope ? "100%" : "auto"}
        justify={useDefaultScope ? "center" : "flex-start"}
      >
        <Grid
          templateColumns="repeat(2, 1fr)"
          gap={5}
          w="100%"
          minW="600px"
          justifyItems={useDefaultScope ? "center" : "stretch"}
        >
          {/* Scope Selection Card */}
          {/* Centered when default scope, top-left when custom scope */}
          <GridItem colSpan={!useDefaultScope && selectedMetricHeaders.length > 0 ? 1 : 2}>
            <Box
              bg="white"
              borderRadius="lg"
              border="1px"
              borderColor="gray.200"
              p={6}
              boxShadow="sm"
              display="flex"
              flexDirection="column"
              h="100%"
              // Control width when centered
              w={useDefaultScope ? "auto" : "100%"}
              minW={useDefaultScope ? "600px" : undefined}
              maxW={useDefaultScope ? "800px" : undefined}
            >
              <VStack align="stretch" spacing={2} flex="1" textAlign="left">
                {/* Metric Selection */}
                {!useDefaultScope && (
                  <>
                    <VStack align="stretch" spacing={2} h="100%" overflow="hidden">
                      <HStack justify="space-between" align="center" flexShrink={0} mb={2}>
                        <HStack spacing={3}>
                          <Circle
                            size="24px"
                            bg="purple.600"
                            color="white"
                            fontWeight="bold"
                            fontSize="sm"
                            flexShrink={0}
                          >
                            1
                          </Circle>
                          <HStack spacing={2}>
                            <Text fontSize="md" fontWeight="700" color="gray.800" mb={0} textAlign="left">
                              Select metrics
                            </Text>
                            <Tooltip
                              label={getTooltip("data-scope-configurator.metric_selection.tooltip") || ""}
                              placement="top"
                              hasArrow
                              bg="gray.700"
                              color="white"
                              fontSize="sm"
                              px={3}
                              py={2}
                              borderRadius="md"
                            >
                              <Box as="span" cursor="help">
                                <Icon as={IoMdInformationCircleOutline} boxSize={6} color="blue.300" />
                              </Box>
                            </Tooltip>
                          </HStack>
                        </HStack>
                        <Menu closeOnSelect={false} autoSelect={false}>
                          <MenuButton
                            as={Button}
                            size="sm"
                            rightIcon={<ChevronDownIcon />}
                            colorScheme="purple"
                            variant="outline"
                            isLoading={isLoadingMetrics}
                            isDisabled={isLoadingMetrics || listOfMetrics.length === 0}
                          >
                            {selectedMetricHeaders.length > 0
                              ? `${selectedMetricHeaders.length} selected`
                              : "Select metrics"}
                          </MenuButton>
                          <Portal>
                            <MenuList maxH="300px" overflowY="auto" minW="280px" px={1} zIndex="dropdown">
                              {isLoadingMetrics ? (
                                <VStack py={4}>
                                  <Spinner size="sm" color="purple.500" />
                                  <Text fontSize="sm" color="gray.500">
                                    Loading metrics...
                                  </Text>
                                </VStack>
                              ) : listOfMetrics.length > 0 ? (
                                listOfMetrics.map((metric) => (
                                  <MenuItem
                                    key={metric.id}
                                    display="flex"
                                    alignItems="flex-start"
                                    gap={2}
                                    py={2}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      toggleMetricSelection(metric);
                                    }}
                                    closeOnSelect={false}
                                    _hover={{ bg: "purple.50" }}
                                    _focus={{ bg: "purple.50" }}
                                  >
                                    <Checkbox
                                      isChecked={isMetricSelected(metric.id)}
                                      colorScheme="purple"
                                      pointerEvents="none"
                                      mt={1}
                                      flexShrink={0}
                                    />
                                    <Text fontSize="sm" fontWeight="500" color="gray.700">
                                      {metric.name}
                                    </Text>
                                  </MenuItem>
                                ))
                              ) : (
                                <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                                  No metrics available
                                </Text>
                              )}
                            </MenuList>
                          </Portal>
                        </Menu>
                      </HStack>

                      {/* Selected Metrics Tags */}
                      {selectedMetricHeaders.length > 0 && (
                        <Box
                          flex="1"
                          overflowY="auto"
                          sx={{
                            "&::-webkit-scrollbar": {
                              width: "6px"
                            },
                            "&::-webkit-scrollbar-track": {
                              bg: "gray.100"
                            },
                            "&::-webkit-scrollbar-thumb": {
                              bg: "gray.300",
                              borderRadius: "full"
                            },
                            "&::-webkit-scrollbar-thumb:hover": {
                              bg: "gray.400"
                            },
                            scrollbarWidth: "thin",
                            scrollbarColor: "var(--chakra-colors-gray-300) var(--chakra-colors-gray-100)"
                          }}
                        >
                          <Wrap spacing={2}>
                            {selectedMetricHeaders.map((metric) => (
                              <WrapItem key={metric.id}>
                                <Tag
                                  size="md"
                                  borderRadius="xl"
                                  colorScheme="purple"
                                  variant="solid"
                                  px={3}
                                  py={2}
                                  bg="purple.50"
                                  borderWidth="1px"
                                  borderColor="purple.300"
                                >
                                  <TagLabel fontWeight="700" color="gray.800">{metric.name}</TagLabel>
                                  <TagCloseButton onClick={() => removeSelectedMetric(metric.id)} color="gray.800" />
                                </Tag>
                              </WrapItem>
                            ))}
                          </Wrap>
                        </Box>
                      )}
                    </VStack>
                  </>
                )}
                {/* Use Default Scope Checkbox */}
                <FormControl>
                  <Checkbox
                    isChecked={useDefaultScope}
                    onChange={(e) => setUseDefaultScope(e.target.checked)}
                    colorScheme="purple"
                    size="lg"
                  >
                    <Text fontSize="md" fontWeight="600" color={!useDefaultScope ? "gray.500" : "gray.800"} mb={0}
                      textAlign="left" textDecoration={!useDefaultScope ? "line-through" : "none"}>
                      Access entire knowledge graph
                    </Text>
                  </Checkbox>
                  <Text fontSize="sm" color="gray.500" fontWeight="500" mt={0} textAlign="left" textDecoration={!useDefaultScope ? "line-through" : "none"}>
                    Grant analyst access to all metrics, benchmarks, and attributes
                  </Text>
                </FormControl>
                {/* Instructions Text Area */}
                {useDefaultScope && (
                  <FormControl>
                    <FormLabel fontSize="md" fontWeight="700" color="gray.700" mb={2} mt={useDefaultScope ? 0 : 1}>
                      Data usage guidelines
                    </FormLabel>
                    <Textarea
                      size="md"
                      value={instructions}
                      onChange={(e) => setInstructions(e.target.value)}
                      placeholder="Enter guidelines or instructions for using this data scope..."
                      rows={4}
                      _focus={{
                        borderColor: "purple.500",
                        boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)"
                      }}
                      fontWeight="500"
                      bg="gray.50"
                      _hover={{ bg: "white" }}
                      resize="none"
                    />
                  </FormControl>
                )}
              </VStack>

              {/* Warning when using default scope */}
              {useDefaultScope && (
                <HStack
                  spacing={3}
                  bg="yellow.50"
                  border="1px"
                  borderColor="yellow.200"
                  borderRadius="md"
                  p={3}
                  mt={3}
                >
                  <WarningIcon color="yellow.600" boxSize={6} />
                  <Text fontSize="sm" color="yellow.800" fontWeight="600" mb={0} textAlign="left">
                    Giving access to the entire knowledge graph may overload the analyst and result in poor performance. It is recommended to refine the data scope to improve performance.
                  </Text>
                </HStack>
              )}
            </Box>
          </GridItem>
          {/* Dependent Data Selection Card (Attributes, timeframes & benchmarks)*/}
          <GridItem>
            {!useDefaultScope && selectedMetricHeaders.length > 0 && (
              <Box
                bg="white"
                borderRadius="lg"
                border="1px"
                borderColor="gray.200"
                p={6}
                boxShadow="sm"
                h="100%"
                display="flex"
                flexDirection="column"
                maxH="500px"
                overflow="hidden"
              >
                <VStack spacing={1} align="stretch" h="100%" overflow="hidden">
                  <Box flexShrink={0}>
                    <HStack spacing={3} mb={1}>
                      <Circle
                        size="24px"
                        bg="purple.600"
                        color="white"
                        fontWeight="bold"
                        fontSize="sm"
                        flexShrink={0}
                      >
                        2
                      </Circle>
                      <HStack spacing={2}>
                        <Text fontSize="md" fontWeight="700" color="gray.800" mb={0} textAlign="left">
                          Scope related entities
                        </Text>
                        <Tooltip
                          label={getTooltip("data-scope-configurator.scope_related_entities.tooltip") || ""}
                          placement="top"
                          hasArrow
                          bg="gray.700"
                          color="white"
                          fontSize="sm"
                          px={3}
                          py={2}
                          borderRadius="md"
                        >
                          <Box as="span" cursor="help">
                            <Icon as={IoMdInformationCircleOutline} boxSize={6} color="blue.300" />
                          </Box>
                        </Tooltip>
                      </HStack>
                    </HStack>
                  </Box>
                  <Box
                    flex="1"
                    overflowY="auto"
                    sx={{
                      "&::-webkit-scrollbar": {
                        width: "6px"
                      },
                      "&::-webkit-scrollbar-track": {
                        bg: "gray.100"
                      },
                      "&::-webkit-scrollbar-thumb": {
                        bg: "gray.300",
                        borderRadius: "full"
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        bg: "gray.400"
                      },
                      scrollbarWidth: "thin",
                      scrollbarColor: "var(--chakra-colors-gray-300) var(--chakra-colors-gray-100)"
                    }}
                  >
                    <VStack spacing={0} align="stretch">
                      {benchmarkList.length === 0 &&
                        measureList.length === 0 &&
                        timeframeList.length === 0 &&
                        attributeList.length === 0 &&
                        (isAnyEdgesLoading ? (
                          <HStack spacing={2} justify="center" py={4}>
                            <Spinner size="sm" color="purple.500" />
                            <Text fontSize="sm" color="gray.500">
                              Loading available data components...
                            </Text>
                          </HStack>
                        ) : (
                          <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                            No data components available for the selected metrics
                          </Text>
                        ))}
                      {renderDependentHeaderMenu(
                        "Attributes",
                        attributeList,
                        selectedAttributes,
                        toggleAttributeSelection
                      )}
                      {timeframeList.length > 0 && (<Divider my={2} />)}
                      {renderDependentHeaderMenu(
                        "Timeframes",
                        timeframeList,
                        selectedTimeframes,
                        toggleTimeframeSelection
                      )}
                      {benchmarkList.length > 0 && (<Divider my={2} />)}
                      {renderDependentHeaderMenu(
                        "Benchmarks",
                        benchmarkList,
                        selectedBenchmarks,
                        toggleBenchmarkSelection
                      )}
                      {measureList.length > 0 && (<Divider my={2} />)}
                      {renderDependentHeaderMenu(
                        "Measures",
                        measureList,
                        selectedMeasures,
                        toggleMeasureSelection
                      )}
                    </VStack>
                  </Box>
                </VStack>
              </Box>
            )}
          </GridItem>
          {/* Edit Filter Section - shown only when editing (inline, not in modal) */}
          <GridItem>
            {/* Applied Filters Card - Only show when filters have values and operator */}
            {!useDefaultScope && selectedMetricHeaders.length > 0 && (
              <Box
                bg="white"
                borderRadius="lg"
                border="1px"
                borderColor="gray.200"
                p={6}
                boxShadow="sm"
                h="100%"
                display="flex"
                flexDirection="column"
                maxH="500px"
                overflow="hidden"
              >
                <VStack align="stretch" spacing={4} h="100%" overflow="hidden">
                  <HStack justify="space-between" align="center">
                    <HStack spacing={3}>
                      <Circle
                        size="24px"
                        bg="purple.600"
                        color="white"
                        fontWeight="bold"
                        fontSize="sm"
                        flexShrink={0}
                      >
                        3
                      </Circle>
                      <HStack spacing={2}>
                        <Text fontSize="md" fontWeight="700" color="gray.800" mb={0}>
                          Scope attribute values
                        </Text>
                        <Tooltip
                          label={getTooltip("data-scope-configurator.attribute_values.tooltip") || ""}
                          placement="top"
                          hasArrow
                          bg="gray.700"
                          color="white"
                          fontSize="sm"
                          px={3}
                          py={2}
                          borderRadius="md"
                        >
                          <Box as="span" cursor="help">
                            <Icon as={IoMdInformationCircleOutline} boxSize={6} color="blue.300" />
                          </Box>
                        </Tooltip>
                      </HStack>
                    </HStack>
                    <Button
                      leftIcon={<Icon as={AddIcon} boxSize={4} />}
                      size="sm"
                      variant="solid"
                      colorScheme="purple"
                      onClick={openFilterModal}
                      fontSize="md"
                      bg="purple.600"
                      _hover={{ bg: "purple.500" }}
                    >
                      Add
                    </Button>
                  </HStack>
                  <Box
                    flex="1"
                    overflowY="auto"
                    sx={{
                      "&::-webkit-scrollbar": {
                        width: "6px"
                      },
                      "&::-webkit-scrollbar-track": {
                        bg: "gray.100"
                      },
                      "&::-webkit-scrollbar-thumb": {
                        bg: "gray.300",
                        borderRadius: "full"
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        bg: "gray.400"
                      },
                      scrollbarWidth: "thin",
                      scrollbarColor: "var(--chakra-colors-gray-300) var(--chakra-colors-gray-100)"
                    }}
                  >
                    <VStack spacing={0} align="stretch">
                      {attributeFilters.filter(f => f.operator !== undefined && f.values && f.values.length > 0).map((filter) => {
                        const actualIndex = columnScopes.findIndex(f => f?.kg_column?.header?.id === filter?.kg_column?.header?.id);
                        return (
                          <React.Fragment key={filter?.kg_column?.header?.id}>
                            <Box
                              bg="transparent"
                              px={0}
                              py={0}
                              transition="all 0.2s"
                            >
                              <Flex gap={3} alignItems="center" justifyContent="space-between">
                                {/* Left side - Filter content */}
                                <Flex
                                  gap={1}
                                  py={2}
                                  alignItems="center"
                                  flex="1"
                                  flexWrap="wrap"
                                  position="relative"  // For the overlay
                                  _before={filter.is_hidden ? {
                                    content: "''",
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(128, 90, 213, 0.1) 8px, rgba(128, 90, 213, 0.1) 16px)",
                                    pointerEvents: "none",  // Allow clicking through
                                    borderRadius: "md",
                                    zIndex: 1
                                  } : {}}
                                >
                                  {filter?.values && filter?.values.length > 0 ? (
                                    // Filter with values
                                    <>
                                      <Tag
                                        size="md"
                                        colorScheme={filter.is_hidden ? "gray" : "purple"}
                                        fontWeight="600"
                                        flexShrink={0}
                                        bg={filter.is_hidden ? "gray.50" : "purple.50"}
                                        borderWidth="1px"
                                        borderColor={filter.is_hidden ? "gray.300" : "purple.300"}
                                        px={2}
                                        py={1}
                                      >
                                        {filter?.kg_column?.header?.name}
                                      </Tag>
                                      <Tag
                                        size="md"
                                        colorScheme="gray"
                                        fontWeight="700"
                                        flexShrink={0}
                                        bg="transparent"
                                        color={filter.is_hidden ? "gray.400" : "gray.600"}
                                      >
                                        {filter.operator === FilterOperator.EQ ? "=" : "≠"}
                                      </Tag>
                                      <Flex
                                        gap={2}
                                        flexWrap="wrap"
                                        alignItems="center"  // Add this
                                        flex="1"  // Add this to take available space
                                        minW="0"  // Add this to allow shrinking
                                      >
                                        {filter?.values?.slice(0, 3).map((value, idx) => (
                                          <Tag
                                            key={idx}
                                            size="md"
                                            variant="outline"
                                            colorScheme={filter.is_hidden ? "gray" : "purple"}
                                            whiteSpace="nowrap"  // Add this to prevent text wrapping inside tag
                                          >
                                            {value}
                                          </Tag>
                                        ))}
                                        {filter?.values?.length > 3 && (
                                          <Tag
                                            size="md"
                                            variant="outline"
                                            colorScheme={filter.is_hidden ? "gray" : "purple"}
                                            fontWeight="600"
                                            whiteSpace="nowrap"  // Add this
                                          >
                                            +{filter.values.length - 3} more
                                          </Tag>
                                        )}
                                      </Flex>
                                    </>
                                  ) : (
                                    // Filter without values (entire column scope)
                                    <Tag
                                      size="md"
                                      colorScheme={filter.is_hidden ? "gray" : "purple"}
                                      fontWeight="600"
                                      flexShrink={0}
                                    >
                                      {filter?.kg_column?.header?.name}
                                    </Tag>
                                  )}
                                </Flex>

                                {/* Right side - Action icons */}
                                {filter?.kg_column?.header?.id && (
                                  <HStack spacing={0} flexShrink={0}>
                                    {/* Edit icon */}
                                    <Tooltip
                                      label={getTooltip("data-scope-configurator.edit_filter_values.tooltip") || ""}
                                      placement="top"
                                      hasArrow
                                      bg="gray.700"
                                      color="white"
                                      fontSize="sm"
                                    >
                                      <IconButton
                                        aria-label="Edit filter"
                                        icon={< EditIcon color="purple.700" />}
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="blue"
                                        onClick={() => openFilterModalForEdit(filter, actualIndex)}
                                      />
                                    </Tooltip>
                                    {/* Eye icon - Toggle visibility */}
                                    {filter?.operator && filter?.values?.length > 0 && (
                                      <Tooltip
                                        label={filter.is_hidden ? getTooltip("data-scope-configurator.show_filter.tooltip") || "" : getTooltip("data-scope-configurator.hide_filter.tooltip") || ""}
                                        placement="top"
                                        hasArrow
                                        bg="gray.700"
                                        color="white"
                                        fontSize="sm"
                                      >
                                        <IconButton
                                          aria-label={filter.is_hidden ? "Show filter" : "Hide filter"}
                                          icon={filter.is_hidden ? <ViewOffIcon /> : <ViewIcon />}
                                          size="sm"
                                          variant="ghost"
                                          colorScheme={filter.is_hidden ? "gray" : "green"}
                                          onClick={() => handleFilterHidden(filter?.kg_column?.header?.id || "")}
                                        />
                                      </Tooltip>
                                    )}
                                    {/* Remove icon */}
                                    <Tooltip
                                      label={getTooltip("data-scope-configurator.remove_filter.tooltip") || ""}
                                      placement="top"
                                      hasArrow
                                      bg="gray.700"
                                      color="white"
                                      fontSize="sm"
                                    >
                                      <IconButton
                                        aria-label="Remove filter"
                                        icon={<DeleteIcon />}
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="red"
                                        color="red.300"
                                        onClick={() => handleRemoveFilter(filter?.kg_column?.header?.id || "")}
                                      />
                                    </Tooltip>
                                  </HStack>
                                )}
                              </Flex>
                            </Box>
                            <Divider my={1} />
                          </React.Fragment>
                        );
                      })}
                    </VStack>
                  </Box>
                </VStack>
              </Box>
            )}
          </GridItem>

          <GridItem>
            {!useDefaultScope && selectedMetricHeaders.length > 0 && (
              <Box
                bg="white"
                borderRadius="lg"
                border="1px"
                borderColor="gray.200"
                p={6}
                boxShadow="sm"
                h="100%"
                display="flex"
                flexDirection="column"
              >
                <VStack align="stretch" spacing={4}>
                  <HStack spacing={3}>
                    <Circle
                      size="24px"
                      bg="purple.600"
                      color="white"
                      fontWeight="bold"
                      fontSize="sm"
                      flexShrink={0}
                    >
                      4
                    </Circle>
                    <HStack spacing={2}>
                      <Text fontSize="md" fontWeight="700" color="gray.800" mb={0} textAlign="left">
                        Usage guidelines
                      </Text>
                      <Tooltip
                        label={getTooltip("data-scope-configurator.usage_guidelines.tooltip") || ""}
                        placement="top"
                        hasArrow
                        bg="gray.700"
                        color="white"
                        fontSize="sm"
                        px={3}
                        py={2}
                        borderRadius="md"
                      >
                        <Box as="span" cursor="help">
                          <Icon as={IoMdInformationCircleOutline} boxSize={6} color="blue.300" />
                        </Box>
                      </Tooltip>
                    </HStack>
                  </HStack>

                  <FormControl>
                    <Textarea
                      size="md"
                      value={instructions}
                      onChange={(e) => setInstructions(e.target.value)}
                      placeholder="Enter guidelines or instructions for using this data scope..."
                      rows={7}
                      _focus={{
                        borderColor: "purple.500",
                        boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)"
                      }}
                      fontWeight="500"
                      bg="gray.50"
                      _hover={{ bg: "white" }}
                      resize="none"
                    />
                  </FormControl>
                </VStack>
              </Box>
            )}
          </GridItem>
        </Grid>
      </VStack>

      {/* Filter Modal for Adding/Editing Filters */}
      <Modal isOpen={isFilterModalOpen} onClose={closeFilterModal} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="650px">
          <ModalHeader>{isEditingExistingFilter ? "Edit Filter" : "Add Filter"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={5} align="stretch">
              {/* Attribute Selection Menu - Disabled when editing existing filter */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={2}>
                  Select Attribute
                </FormLabel>
                <Menu>
                  <MenuButton
                    as={Button}
                    rightIcon={isEditingExistingFilter ? undefined : <ChevronDownIcon />}
                    w="100%"
                    textAlign="left"
                    variant="outline"
                    fontWeight="500"
                    isDisabled={isLoadingValues || isEditingExistingFilter}
                    cursor={isEditingExistingFilter ? "default" : "pointer"}
                  >
                    {editingFilter?.kg_column?.header?.name || "Choose an attribute"}
                  </MenuButton>
                  {!isEditingExistingFilter && (
                    <Portal>
                      <MenuList maxH="250px" overflowY="auto" minW="280px" zIndex={1500}>
                        {selectedAttributes.map((attr) => {
                          const filterIndex = columnScopes.findIndex(f => f?.kg_column?.header?.id === attr.id);
                          const filter = columnScopes[filterIndex];

                          return (
                            <MenuItem
                              key={attr.id}
                              onClick={() => filter && handleAttributeSelectionInModal(filter, filterIndex)}
                              _hover={{ bg: "purple.50" }}
                              _focus={{ bg: "purple.50" }}
                            >
                              <Text fontSize="sm" fontWeight="500" color="gray.700">
                                {attr.name}
                              </Text>
                            </MenuItem>
                          );
                        })}
                      </MenuList>
                    </Portal>
                  )}
                </Menu>
              </FormControl>

              {/* Loading state when fetching values */}
              {isLoadingValues && (
                <Flex justify="center" align="center" py={6}>
                  <Spinner size="md" mr={3} color="purple.500" />
                  <Text fontSize="md" color="gray.600">Loading values...</Text>
                </Flex>
              )}

              {/* Value Selection - Show when attribute is selected and values loaded */}
              {editingFilter && showValuesSection && !isLoadingValues && (
                <VStack spacing={4} align="stretch">
                  <HStack spacing={4} align="flex-start">
                    {/* Operator Selection */}
                    <FormControl maxW="160px">
                      <FormLabel fontSize="sm" fontWeight="600" mb={2} color="gray.700">
                        Operator
                      </FormLabel>
                      <Select
                        size="md"
                        value={selectedFilterOperator}
                        onChange={(e) => setSelectedFilterOperator(Number(e.target.value) as FilterOperator)}
                        fontWeight="500"
                        bg="white"
                      >
                        <option value={FilterOperator.EQ}>Equals (=)</option>
                        <option value={FilterOperator.NE}>Not Equals (≠)</option>
                      </Select>
                    </FormControl>

                    {/* Values Multi-Select */}
                    <FormControl flex="1">
                      <FormLabel fontSize="sm" fontWeight="600" mb={2} color="gray.700">
                        Select Values
                      </FormLabel>
                      <ColumnValues
                        contextValues={valuesList}
                        selectedOptions={selectedFilterValues}
                        setSelectedOptions={setSelectedFilterValues}
                        errorMsg=""
                      />
                    </FormControl>
                  </HStack>

                  {/* Action Buttons */}
                  <HStack justify="flex-end" spacing={3} pt={2}>
                    <Button variant="ghost" onClick={closeFilterModal}>
                      Cancel
                    </Button>
                    <Button
                      colorScheme="purple"
                      onClick={handleSaveFilterFromModal}
                      isDisabled={selectedFilterValues.length === 0}
                    >
                      {isEditingExistingFilter ? "Save" : "Add"}
                    </Button>
                  </HStack>
                </VStack>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DataScopeConfigurator;